import React, {useEffect, useMemo, useState} from "react";
import axios from "axios";
import {toast} from "react-toastify";
import {useNavigate, useSearchParams} from 'react-router-dom';
// @ts-ignore
import {MagnifyingGlass, Plus, Trash, FileText, Sparkle} from '@phosphor-icons/react';
import {getBaseUrlNodeServer} from "../../config";
import {renderMjml} from "../../lib/mjmlPreview";
import {inferCategory} from "../../lib/templateCategories";

// Card preview iframe — renders the actual rendered email HTML, scaled down.
const PreviewFrame = ({html}) => (
	<div className="relative w-full overflow-hidden bg-slate-50" style={{aspectRatio: '16 / 10'}}>
		<iframe
			title="template-preview"
			srcDoc={html || '<html><body style="margin:0;background:#f8fafc;"></body></html>'}
			sandbox=""
			className="absolute top-0 left-0 pointer-events-none border-0"
			style={{
				transform: 'scale(0.4)',
				transformOrigin: 'top left',
				width: '250%',
				height: '250%',
			}}/>
	</div>
);

const SkeletonCard = () => (
	<div className="rounded-2xl border border-slate-100 bg-white shadow-sm overflow-hidden animate-pulse">
		<div className="bg-slate-100" style={{aspectRatio: '16 / 10'}}/>
		<div className="p-4">
			<div className="h-4 w-3/4 bg-slate-100 rounded mb-3"/>
			<div className="flex gap-2">
				<div className="h-5 w-14 bg-slate-100 rounded-full"/>
				<div className="h-5 w-16 bg-slate-100 rounded-full"/>
			</div>
		</div>
	</div>
);

// Category inference lives in src/lib/templateCategories.js so the in-editor
// preview strip can share the same rules.

const EmptyState = ({onCreate, onBrowsePremium}) => (
	<div className="rounded-2xl border border-slate-100 bg-white shadow-sm py-16 px-6 text-center">
		<div className="mx-auto w-16 h-16 rounded-2xl bg-emerald-50 border border-emerald-200 flex items-center justify-center mb-5">
			<FileText size={32} weight="duotone" color="#10B981"/>
		</div>
		<h3 className="text-xl font-semibold text-slate-900 mb-2">No templates yet</h3>
		<p className="text-base text-slate-600 mb-6 max-w-md mx-auto">
			Start with one of our premium templates or design your own from a blank canvas.
		</p>
		<div className="flex flex-wrap justify-center gap-3">
			<button
				type="button"
				onClick={onBrowsePremium}
				className="inline-flex items-center gap-2 rounded-full px-6 py-3 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold shadow-sm transition-colors">
				<Sparkle size={18} weight="bold"/>
				Browse premium templates
			</button>
			<button
				type="button"
				onClick={onCreate}
				className="inline-flex items-center gap-2 rounded-full px-6 py-3 bg-white border border-slate-200 hover:bg-slate-50 text-slate-900 font-medium transition">
				<Plus size={18} weight="bold"/>
				Start from blank
			</button>
		</div>
	</div>
);

export default function SavedTemplates() {
	const [isLoading, setIsLoading] = useState(true);
	const [templates, setTemplates] = useState([]);
	const navigate = useNavigate();
	// URL-backed filter state: ?tag=Welcome&q=foo. Lets the editor's "back
	// to templates" return to the same filter, and makes filtered views
	// shareable.
	const [searchParams, setSearchParams] = useSearchParams();
	const activeCategory = searchParams.get('tag') || 'all';
	const search = searchParams.get('q') || '';
	function setActiveCategory(next) {
		const sp = new URLSearchParams(searchParams);
		if (!next || next === 'all') sp.delete('tag'); else sp.set('tag', next);
		setSearchParams(sp, {replace: true});
	}
	function setSearch(next) {
		const sp = new URLSearchParams(searchParams);
		if (!next) sp.delete('q'); else sp.set('q', next);
		setSearchParams(sp, {replace: true});
	}

	useEffect(() => {
		window.scrollTo(0, 0);

		async function loadTemplates() {
			try {
				const verifiedemail = sessionStorage.getItem("verifiedemail");
				axios.defaults.baseURL = getBaseUrlNodeServer();
				const response = await axios.post('getusersavedtemplates', {'verifiedemail': verifiedemail});
				const usertemplates = Array.isArray(response.data) ? response.data : [];

				// Render MJML → HTML in the browser (mjml-browser, memoized)
				// instead of N round-trips to the server's /mjml2html endpoint.
				// Was 200–500ms per template + network → now <50ms each, sync.
				const enriched = usertemplates.map((template) => {
					const userCats = Array.isArray(template.selectedCategories)
						? template.selectedCategories.filter(Boolean)
						: [];
					const categories = userCats.length > 0
						? userCats
						: [inferCategory(template.subject, template.content)];
					return {
						_id: template._id,
						subject: template.subject || 'Untitled template',
						html: renderMjml(template.content || ''),
						categories,
					};
				});

				setTemplates(enriched);
			} catch (err) {
				console.error(err);
				toast.error('Failed to load your templates');
			} finally {
				setIsLoading(false);
			}
		}

		loadTemplates();
	}, []);

	const allCategories = useMemo(() => {
		const set = new Set();
		templates.forEach(t => (t.categories || []).forEach(c => c && set.add(c)));
		return Array.from(set);
	}, [templates]);

	const filtered = useMemo(() => {
		const q = search.trim().toLowerCase();
		return templates.filter(t => {
			if (activeCategory !== 'all' && !(t.categories || []).includes(activeCategory)) return false;
			if (q && !(t.subject || '').toLowerCase().includes(q)) return false;
			return true;
		});
	}, [templates, search, activeCategory]);

	async function userWantsToDelete(e, templateId) {
		e.stopPropagation();
		if (!window.confirm("Delete this template? This can't be undone.")) return;
		try {
			const verifiedemail = sessionStorage.getItem("verifiedemail");
			axios.defaults.baseURL = getBaseUrlNodeServer();
			await axios.post('deleteusertemplate',
				{'verifiedemail': verifiedemail, 'templateId': templateId});
			setTemplates(prev => prev.filter(t => t._id !== templateId));
			toast.success("Template deleted");
		} catch (err) {
			console.error(err);
			toast.error("Failed to delete template");
		}
	}

	function userWantsToEdit(templateId) {
		const signedin = sessionStorage.getItem("signedin");
		if (signedin) {
			navigate('/editor/' + templateId);
		} else {
			toast.error("Login to edit the template");
		}
	}

	function startBlank() {
		navigate('/editor');
	}

	return (
		<div className="bg-white min-h-screen">
			<div className="max-w-7xl mx-auto px-6 py-10">
				{/* Header */}
				<div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
					<div>
						<h1 className="text-3xl md:text-4xl font-semibold text-slate-900 tracking-tight">My templates</h1>
						<p className="text-base text-slate-600 mt-1">
							{isLoading ? 'Loading your saved designs…'
								: templates.length === 0 ? 'Your saved designs will appear here.'
									: `${templates.length} ${templates.length === 1 ? 'template' : 'templates'} saved`}
						</p>
					</div>
					{(isLoading || templates.length > 0) && (
						<button
							type="button"
							onClick={startBlank}
							className="inline-flex items-center gap-2 self-start md:self-auto rounded-full px-6 py-3 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold shadow-sm transition-colors">
							<Plus size={18} weight="bold"/>
							New template
						</button>
					)}
				</div>

				{isLoading ? (
					<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
						{Array.from({length: 6}).map((_, i) => <SkeletonCard key={i}/>)}
					</div>
				) : templates.length === 0 ? (
					<EmptyState
						onCreate={startBlank}
						onBrowsePremium={() => navigate('/alltemplates')}/>
				) : (
					<>
						{/* Filter tags — prominent row across the top of the content area */}
						{allCategories.length > 0 && (
							<div className="mb-5 rounded-2xl border border-slate-100 bg-slate-50/60 px-4 py-3">
								<div className="flex flex-wrap items-center gap-2">
									<span className="text-xs font-semibold uppercase tracking-wide text-slate-500 mr-1">Filter</span>
									<button
										type="button"
										onClick={() => setActiveCategory('all')}
										className={
											'inline-flex items-center rounded-full px-4 py-1.5 text-sm font-medium transition-colors border ' +
											(activeCategory === 'all'
												? 'bg-emerald-500 text-white border-emerald-500 shadow-sm hover:bg-emerald-600'
												: 'bg-white text-slate-700 border-slate-200 hover:border-emerald-200 hover:text-emerald-700')
										}>
										All
										<span className={'ml-2 text-xs ' + (activeCategory === 'all' ? 'text-emerald-50' : 'text-slate-400')}>
											{templates.length}
										</span>
									</button>
									{allCategories.map((cat) => {
										const count = templates.filter(t => (t.categories || []).includes(cat)).length;
										const active = activeCategory === cat;
										return (
											<button
												key={cat}
												type="button"
												onClick={() => setActiveCategory(cat)}
												className={
													'inline-flex items-center rounded-full px-4 py-1.5 text-sm font-medium transition-colors border ' +
													(active
														? 'bg-emerald-500 text-white border-emerald-500 shadow-sm hover:bg-emerald-600'
														: 'bg-white text-slate-700 border-slate-200 hover:border-emerald-200 hover:text-emerald-700')
												}>
												{cat}
												<span className={'ml-2 text-xs ' + (active ? 'text-emerald-50' : 'text-slate-400')}>
													{count}
												</span>
											</button>
										);
									})}
								</div>
							</div>
						)}

						{/* Search */}
						<div className="flex justify-end mb-6">
							<div className="relative w-full md:w-72">
								<MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
								<input
									type="text"
									value={search}
									onChange={(e) => setSearch(e.target.value)}
									placeholder="Search templates…"
									className="w-full pl-9 pr-3 border border-slate-200 rounded-xl py-2.5 focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100 outline-none transition text-sm"/>
							</div>
						</div>

						{filtered.length === 0 ? (
							<div className="rounded-2xl border border-slate-100 bg-white shadow-sm py-16 px-6 text-center">
								<p className="text-base text-slate-600">No templates match this filter.</p>
							</div>
						) : (
							<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
								{filtered.map((t) => (
									<div
										key={t._id}
										onClick={() => userWantsToEdit(t._id)}
										className="group relative rounded-2xl border border-slate-100 bg-white shadow-sm hover:shadow-lg transition cursor-pointer overflow-hidden">
										<PreviewFrame html={t.html}/>

										{/* Footer: subject + inline tags on the left, Delete on the right */}
										<div className="p-4 border-t border-slate-100 flex items-start gap-3">
											<div className="min-w-0 flex-1">
												<div className="font-medium text-slate-900 truncate" title={t.subject}>{t.subject}</div>
												{t.categories && t.categories.length > 0 && (
													<div className="flex flex-wrap gap-1.5 mt-2">
														{t.categories.slice(0, 3).map((c) => (
															<span
																key={c}
																className="inline-flex items-center rounded-full bg-emerald-50 border border-emerald-200 text-emerald-800 px-2 py-0.5 text-xs font-medium">
																{c}
															</span>
														))}
														{t.categories.length > 3 && (
															<span className="inline-flex items-center text-xs text-slate-500">
																+{t.categories.length - 3}
															</span>
														)}
													</div>
												)}
											</div>
											<button
												type="button"
												onClick={(e) => userWantsToDelete(e, t._id)}
												title="Delete template"
												aria-label="Delete template"
												className="shrink-0 inline-flex items-center justify-center w-9 h-9 rounded-full text-slate-400 hover:text-red-600 hover:bg-red-50 transition">
												<Trash size={16} weight="bold"/>
											</button>
										</div>
									</div>
								))}
							</div>
						)}
					</>
				)}
			</div>
		</div>
	);
}
